import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "../api";
import type { Datified, Schemas } from "../../api/types";
import { POLL_WATCH_MS } from "./intervals";

export type WorkerToken = Datified<
  Schemas["WorkerToken"],
  "created_at" | "last_seen_at"
>;
export type WorkerTokenCreate = Schemas["WorkerTokenCreate"];
export type WorkerTokenCreated = WorkerToken & { token: string };
export type Channel = Schemas["WorkerTokenCreate"]["channels"][number];

/**
 * `includeDeleted` is the page's "show deleted" switch. The flag is part of
 * the query key, so the two views are cached side by side instead of one
 * overwriting the other on every toggle.
 */
export function useWorkerTokenList(includeDeleted = false) {
  return useQuery<WorkerToken[]>({
    queryKey: ["worker-tokens", { includeDeleted }],
    queryFn: async () => {
      const res = await apiGet<{ items: WorkerToken[] }>(
        includeDeleted ? "/worker-tokens?include_deleted=true" : "/worker-tokens",
      );
      return res.items;
    },
    refetchInterval: POLL_WATCH_MS,
  });
}

export type WorkerInstance = Datified<
  Schemas["WorkerInstance"],
  "first_seen_at" | "last_seen_at" | "last_claim_at" | "last_error_at"
>;

/** GET /api/worker-instances — worker processes, most recently seen first. */
export function useWorkerInstances() {
  return useQuery<WorkerInstance[]>({
    queryKey: ["worker-instances"],
    queryFn: async () => {
      const res = await apiGet<{ items: WorkerInstance[] }>("/worker-instances");
      return res.items;
    },
    refetchInterval: POLL_WATCH_MS,
  });
}

export function useCreateWorkerToken() {
  const qc = useQueryClient();
  return useMutation<WorkerTokenCreated, unknown, WorkerTokenCreate>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPost<WorkerTokenCreated>("/worker-tokens", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["worker-tokens"] }),
  });
}

export function useRevokeWorkerToken() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { id: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id }) => apiPost<void>(`/worker-tokens/${id}/revoke`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["worker-tokens"] }),
  });
}

/** Only ever called for a revoked token; the server refuses an active one. */
export function useDeleteWorkerToken() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { id: string }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id }) => apiDelete<void>(`/worker-tokens/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["worker-tokens"] });
      // Its instances went with it.
      void qc.invalidateQueries({ queryKey: ["worker-instances"] });
    },
  });
}
